import {
    CardFragment,
    ContentMatrixFragment,
    MenuItemsFragment,
    PageCardFragment,
    PageDocument,
    PageFragment,
    PagePathsDocument,
    PagePathsQuery,
    PageQuery,
    PageQueryVariables,
    TilesBlockFragment,
} from '../../generated/graphql';
import Seo from '../Seo';
import Hero from '../Hero';
import { heroTransformToProps } from '../../constants/imageTransforms';
import ContentWrapper from '@oberoninternal/travelbase-website/dist/components/ContentWrapper';
import { Box } from '@rebass/grid';
import Breadcrumb from '../Breadcrumb';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import ContentMatrix from '../ContentMatrix';
import ShareButton from '../ShareButton';
import ReadMore from '../ReadMore';
import React, { FC, useMemo } from 'react';
import { useRouter } from 'next/router';
import useAnalyticsContentCategory from '@oberoninternal/travelbase-website/dist/hooks/analytics/useAnalyticsContentCategory';
import SearchWrapper from '../layout/SearchWrapper';
import PageWrapper from '@oberoninternal/travelbase-website/dist/components/Page';
import { gql } from '@apollo/client';
import initApolloClient, { PreviewData } from '../../initApolloClient';
import { GetStaticPaths, GetStaticProps } from 'next';
import { addApolloState } from '@oberoninternal/travelbase-website/dist/createApolloClient';
import addGlobalQuery from '../../utils/addGlobalQuery';
import { defaultLanguage } from '../../pages/_app';
import { DEFAULT_REVALIDATION_TIME } from '../../constants/revalidationTime';
import popularPages from '../../constants/popularPages';
import brandConfig from '../../constants/brandConfig';
import getCurrentBaseUrl from '../../utils/getCurrentBaseUrl';
import styled from 'styled-components';
import sentry from '@oberoninternal/travelbase-website/dist/utils/sentry';
import Head from 'next/head';
import hasRedirect from '../../utils/hasRedirect';

interface Props {
    entry: PageFragment;
    menu?: MenuItemsFragment[];
}

export const query = gql`
    query Page($uri: String, $lang: String) {
        entry(uri: [$uri], site: [$lang]) {
            ...Page
        }
    }
    fragment Page on page_Entry {
        uri
        title
        settingsCookieConsent
        settingsMetaDescription
        settingsSeoTitle
        settingsNotSearchable
        settingsNoIndex
        heroImage {
            ...HeroTransform
        }
        root
        contentMatrix {
            ...ContentMatrix
        }

        children {
            ...Card
        }

        id
        parent {
            id

            children(section: ["pages"]) {
                ...Card
            }
        }
        settingsSearchTags {
            title
        }

        localized {
            url
            language
        }
    }

    query PagePaths {
        entries(site: ["*"], type: ["page"]) {
            __typename
            uri
            slug
            language
        }
    }
`;

const Page: FC<React.PropsWithChildren<Props>> = ({ entry, menu }) => {
    const { asPath, locale } = useRouter();
    const baseUrl = getCurrentBaseUrl(locale);
    const pageSiblings = useMemo(
        () =>
            entry.parent?.children
                ?.filter((child): child is CardFragment & PageCardFragment => child?.__typename === 'page_Entry')
                .filter(page => !page.settingsNotSearchable)
                .filter(page => page.uri !== entry.uri) ?? [],
        [entry]
    );
    const pageChildren = useMemo(
        () =>
            entry.children
                ?.filter((child): child is PageCardFragment => child?.__typename === 'page_Entry')
                .filter(page => !page.settingsNotSearchable) ?? [],
        [entry]
    );
    const matrix = entry.contentMatrix?.filter((field): field is ContentMatrixFragment => !!field) ?? [];
    const tileBlocks = matrix.filter((item): item is TilesBlockFragment => item.__typename === 'sectionTiles_Entry');
    const ignoredUris = useMemo(() => {
        const ignoredCards = (tileBlocks.flatMap(block => block.tilesEntries) ?? []) as CardFragment[];
        if (tileBlocks.some(block => block.tilesSiblings)) {
            ignoredCards.push(...pageSiblings);
        }
        if (tileBlocks.some(block => block.tilesChildren)) {
            ignoredCards.push(...pageChildren);
        }
        return ignoredCards.map(card => `/${card.uri}`);
    }, [pageChildren, pageSiblings, tileBlocks]);

    const hasAccoBlocks = matrix.some(item => item.__typename === 'sectionAccommodations_Entry');

    useAnalyticsContentCategory(
        // if the page has an acco block, <Search /> will also call useAnalyticsContentCategory, which is enough.
        !hasAccoBlocks ? 'page' : undefined
    );
    const isHeroVisible = entry.heroImage?.[0];
    // eslint-disable-next-line react/jsx-no-useless-fragment
    const Wrapper: FC<React.PropsWithChildren<unknown>> = hasAccoBlocks
        ? SearchWrapper
        : ({ children }) => <>{children}</>;
    const heroImage = entry.heroImage?.[0];

    return (
        <PageWrapper>
            <Seo
                image={heroImage?.ogImageUrl ?? ''}
                settingsMetaDescription={entry.settingsMetaDescription ?? ''}
                settingsSeoTitle={entry.settingsSeoTitle ?? entry.title ?? ''}
                alternates={entry.localized}
            />
            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
            {isHeroVisible && <Hero {...heroTransformToProps(entry.heroImage![0]!)} />}

            {entry.settingsNoIndex ? (
                <Head>
                    <meta name="robots" content="noindex,nofollow" />
                </Head>
            ) : null}

            <Wrapper>
                <ContentWrapper>
                    <Box mt={[3, 8]} mb={[3, 5]}>
                        <Breadcrumb menu={menu} uri={entry.uri} />

                        <Title variant="large">{entry.title}</Title>
                    </Box>
                </ContentWrapper>
                <StyledBox mb={[5, 8]}>
                    <ContentMatrix matrix={matrix} pageSiblings={pageSiblings} pageChildren={pageChildren} />
                </StyledBox>

                <ContentWrapper>
                    <ShareButton
                        share={{
                            title: entry?.title ?? '',
                            text: entry.settingsMetaDescription ?? '',
                            url: baseUrl + asPath,
                        }}
                        image={entry.heroImage?.[0]?.ogImageUrl ?? ''}
                    />
                </ContentWrapper>

                {brandConfig.components?.pages?.readMore && (
                    <ContentWrapper>
                        <ReadMore
                            id={entry.id ? parseInt(entry.id, 10) : undefined}
                            parentId={entry.parent?.id ? parseInt(entry.parent?.id, 10) : undefined}
                            searchTags={entry.settingsSearchTags?.map(item => item?.title ?? '') ?? []}
                            ignoredUris={ignoredUris}
                        />
                    </ContentWrapper>
                )}
            </Wrapper>
        </PageWrapper>
    );
};

export const getStaticProps: GetStaticProps = async context => {
    const { params = {}, previewData } = context;
    const lang = context.locale as string;
    const client = initApolloClient(lang, { previewData: previewData as PreviewData });
    const uri = (params.uri as string[]).join('/');

    const { data, error } = await client.query<PageQuery, PageQueryVariables>({
        query: PageDocument,
        variables: {
            uri,
            lang,
        },
    });

    if (error) {
        const { captureException } = sentry();
        captureException(error, undefined);
        throw new Error(`Failed to query page "${uri}", received message: ${error.message}`);
    }

    if (!data.entry) {
        const redirect = await hasRedirect((params?.uri as string[]).join('/'), lang);

        if (redirect) {
            const { redirectDestUrl, redirectHttpCode } = redirect;
            return {
                redirect: {
                    permanent: redirectHttpCode === 301,
                    destination: redirectDestUrl,
                },
                revalidate: DEFAULT_REVALIDATION_TIME,
            };
        }

        return {
            notFound: true,
        };
    }

    const suppressCookieConsent = !(
        (data.entry as { settingsCookieConsent?: boolean })?.settingsCookieConsent ?? false
    );

    return {
        ...addApolloState(
            client,
            (await addGlobalQuery(
                {
                    props: {
                        entry: data.entry,
                        suppressCookieConsent,
                    },
                },
                context.locale
            )) as { props: Record<string, unknown> }
        ),
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

export const getStaticPaths: GetStaticPaths = async () => {
    // we pre-render only high-traffic pages, so first visitors don't have to wait for it to build (slows down build)
    if (popularPages.length === 0) {
        return { paths: [], fallback: 'blocking' };
    }

    const client = initApolloClient(defaultLanguage);
    const { data } = await client.query<PagePathsQuery>({
        query: PagePathsDocument,
    });

    const paths =
        data.entries
            ?.filter(entry => entry && popularPages.includes(`/${entry.uri}/`))
            .map(entry => {
                const uri = entry?.uri?.split('/') ?? [];
                return {
                    params: {
                        locale: entry?.language ?? defaultLanguage,
                        uri,
                    },
                };
            }) ?? [];

    // eslint-disable-next-line no-console
    console.log(`Page.getStaticPaths: including ${paths.length} out of ${data.entries?.length ?? 0} paths`);

    return {
        paths,
        fallback: 'blocking',
    };
};

const StyledBox = styled(Box)`
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        [class*='Carousel__ScrollWrapper-'] > *:first-child {
            padding-left: var(--wrapperPadding);
        }
        [class*='Carousel__HeaderWrapper-'] {
            padding: 0 var(--wrapperPadding);
        }
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xl}) {
        [class*='Carousel__ScrollWrapper-'] > *:first-child {
            padding-left: var(--paddingLeft);
        }
        [class*='ContentMatrix__Container-'] > [class*='Carousel__HeaderWrapper-'] {
            padding: 0 var(--paddingLeft);
        }
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xxxl}) {
        [class*='Carousel__ScrollWrapper-'] > *:first-child {
            padding-left: calc((100vw - (140.8rem + var(--wrapperPadding) * 2)) / 2 + var(--wrapperPadding));
        }
    }
`;

export default Page;
